import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Table state (search / sort / filters) persisted in the URL with a per-table prefix,
 * so a filtered admin view can be shared and reloaded. `defaults` must be a stable
 * constant at the call site.
 */
export default function useTableState(prefix, defaults = {}) {
  const [params, setParams] = useSearchParams();

  const get = useCallback(
    (k) => params.get(`${prefix}_${k}`) ?? defaults[k] ?? "",
    [params, prefix] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const set = useCallback(
    (k, v) => {
      setParams((cur) => {
        const next = new URLSearchParams(cur);
        const key = `${prefix}_${k}`;
        if (v === "" || v == null || v === defaults[k]) next.delete(key);
        else next.set(key, String(v));
        return next;
      }, { replace: true });
    },
    [prefix, setParams] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toggleSort = useCallback(
    (k, defaultDir = "asc") => {
      setParams((cur) => {
        const next = new URLSearchParams(cur);
        if (cur.get(`${prefix}_sort`) === k) {
          next.set(`${prefix}_dir`, (cur.get(`${prefix}_dir`) || defaultDir) === "asc" ? "desc" : "asc");
        } else {
          next.set(`${prefix}_sort`, k);
          next.set(`${prefix}_dir`, defaultDir);
        }
        return next;
      }, { replace: true });
    },
    [prefix, setParams]
  );

  const reset = useCallback(() => {
    setParams((cur) => {
      const next = new URLSearchParams();
      for (const [k, v] of cur.entries()) if (!k.startsWith(`${prefix}_`)) next.set(k, v);
      return next;
    }, { replace: true });
  }, [prefix, setParams]);

  return useMemo(() => ({ prefix, get, set, toggleSort, reset }), [prefix, get, set, toggleSort, reset]);
}
